import { useEffect } from "react";
import classNames from "classnames";
import { DateFormatType, formatDate } from "../../../utils/DateFormatter";

import { Checkbox, IconButton } from "@mui/material";
import { type NotificationContext, type NotificationData, Notifications } from "../../Messages";
import { useBoolMap } from "~/common/hooks/useBoolMap";

import classes from "./NotificationsList.module.less";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { LinkButton } from "~/common/components/Link";
import { useTranslation } from "react-i18next";

interface NotificationsTableProps {
  data: NotificationData[];
  className?: string;
  onDeleteItems: (uid: string | string[]) => void;
  onMarkAsRead: (uid: string | string[], isRead: boolean) => void;
}

export const NotificationsList = ({ data, className, onDeleteItems, onMarkAsRead }: NotificationsTableProps) => {
  const { t } = useTranslation();

  const context: NotificationContext = {
    markAsRead: async (item, isRead = true) => {
      if (item.read !== isRead) {
        onMarkAsRead(item.uid, isRead);
      }
    },
  };

  const { map: selectedItems, toggle: setSelected, replace } = useBoolMap();

  // Clear the selection if data changes
  const dataUid = data.map((_) => _.uid).join("-");
  useEffect(() => {
    replace({});
  }, [replace, dataUid]);

  const enableButtons = Object.values(selectedItems).some((val) => val);

  const isAllSelected = data.length > 0 && data.every((item) => selectedItems[item.uid]);
  const isAnyChecked = enableButtons;

  const toggleSelectAll = () => {
    const newVal = !isAllSelected;
    const newSelected = data.map((item) => item.uid);
    setSelected(newSelected, newVal);
  };
  const readSelected = () => {
    const selectedUids = Object.entries(selectedItems)
      .filter(([_, selected]) => selected)
      .map(([uid, _]) => uid);
    onMarkAsRead(selectedUids, true);
  };
  const deleteSelected = () => {
    const selectedUids = Object.entries(selectedItems)
      .filter(([_, selected]) => selected)
      .map(([uid, _]) => uid);
    onDeleteItems(selectedUids);
  };

  return (
    <div className={classNames(className, classes.root)}>
      <div className={classes.header}>
        <Checkbox
          checked={isAnyChecked}
          indeterminate={isAnyChecked && !isAllSelected}
          onChange={toggleSelectAll}
          data-test-id={"checkbox-notifications-check-all"}
        />
        <LinkButton
          iconBefore={<Icon type={IconType.EnvelopeOpen} />}
          data-test-id={"read-selected"}
          disabled={!enableButtons}
          onClick={readSelected}
        >
          {t("notificationsPage.actions.markAsRead")}
        </LinkButton>
        <LinkButton
          iconBefore={<Icon type={IconType.Bin} />}
          data-test-id={"delete-selected"}
          disabled={!enableButtons}
          onClick={deleteSelected}
        >
          {t("notificationsPage.actions.delete")}
        </LinkButton>
      </div>
      <div className={classes.itemContainer}>
        {data.map((row, index) => (
          <Item
            key={row.uid}
            data={row}
            context={context}
            selected={selectedItems[row.uid] ?? false}
            onDelete={() => onDeleteItems(row.uid)}
            onSelect={(newVal) => setSelected(row.uid, newVal)}
          />
        ))}
      </div>
    </div>
  );
};

interface ItemProps {
  data: NotificationData;
  context: NotificationContext;
  selected: boolean;
  onSelect: (newValue: boolean) => void;
  onDelete: () => void;
}

const Item = ({ selected, data, context, onSelect, onDelete }: ItemProps) => {
  const { t } = useTranslation();

  return (
    <div className={classNames(classes.item, !data.read && classes.unreadItem)}>
      <div className={classes.itemCheckbox} data-test-id={"notification-selected"}>
        <Checkbox checked={selected} onChange={(e) => onSelect(e.target.checked)} sx={{ mt: -1 }} />
      </div>
      <div className={classes.itemCategory} data-test-id={"notification-type"}>
        {t(data.type)}
      </div>
      <div className={classes.itemTitle} data-test-id={"notification-title"}>
        {Notifications.renderTitle(data, context)}
      </div>
      <div className={classes.itemText} data-test-id={"notification-text"}>
        {Notifications.renderDescription(data, context)}
      </div>
      <div className={classes.itemDate}>{formatDate(new Date(data.notificationDate), DateFormatType.DATE)}</div>
      <div className={classes.itemActions}>
        <IconButton
          color={"primary"}
          onClick={() => context.markAsRead(data, !data.read)}
          data-test-id={"read-notification-btn"}
        >
          <Icon type={data.read ? IconType.Envelope : IconType.EnvelopeOpen} />
        </IconButton>
        <IconButton color={"primary"} onClick={onDelete} data-test-id={"delete-notification-btn"}>
          <Icon type={IconType.Bin} />
        </IconButton>
      </div>
    </div>
  );
};
